import { HISTORY_VIEWS_RESTORED98, navigationIndex98, safeReturn98 } from "./navigationHistory98";
import { recordSpendingFocus } from "./workspaces/project/spendingNavigation";
import { saveNavigationScroll, useNavigationScroll } from "./workspaces/shared/navigationScroll98";
import { DraftNavigationProvider } from "./workspaces/shared/DraftNavigationProvider";
import { userMessage } from "./workspaces/shared/userMessage";
import { parseAppRoute, appRouteHash, type AppRoute } from "./appRoutes";
import { useCallback, useEffect, useRef, useState } from "react";
import { openAppDatabase, type AppDatabase } from "./db/database";
import { AppDbContext } from "./AppContext";
import { SignInScreen } from "./session/SignInScreen";
import { AcceptInvitationScreen, ForgotPasswordPanel, ResetPasswordScreen } from "./session/OnboardingScreens";
import { PrivacyNotice } from "./session/PrivacyNotice";
import { AccountScreen } from "./session/AccountScreen";
import { BetaErrorBoundary } from "./session/BetaChrome";
import { AccountMenu } from "./session/AccountMenu";
import { FeedbackDialog } from "./feedback/FeedbackDialog";
import { enableTelemetry, flushTelemetry, installGlobalDiagnostics, track } from "./telemetry/telemetry";
import { fetchPublicConfig, fetchSession, logout, type AccountState, type HostedAccount } from "./session/session";
import { setCurrentAccountId } from "./session/currentUser";
import { ProjectsScreen } from "./workspaces/projects/ProjectsScreen";
import { BrandMark } from "./workspaces/shared/ui";
import { Lazy, lazyScreen } from "./workspaces/shared/lazyScreen";
import type { ComponentProps } from "react";
import type { AdminScreen as AdminScreenType } from "./admin/AdminScreen";
import type { BusinessFinancesScreen as BusinessFinancesScreenType } from "./workspaces/business/BusinessFinancesScreen";
import type { PortfolioScreen as PortfolioScreenType } from "./workspaces/portfolio/PortfolioScreen";
import type { DealsScreen as DealsScreenType } from "./workspaces/deals/DealsScreen";
import type { FindScreen as FindScreenType } from "./workspaces/find/FindScreen";
import type { ProjectWorkspace as ProjectWorkspaceType } from "./workspaces/project/ProjectWorkspace";
import type { SharedProjectScreen as SharedProjectScreenType } from "./workspaces/shared/SharedProjectScreen";
import type { StandardsScreen as StandardsScreenType } from "./workspaces/standards/StandardsScreen";

const AdminScreen = lazyScreen<ComponentProps<typeof AdminScreenType>>(() => import("./admin/AdminScreen"), "AdminScreen");
const BusinessFinancesScreen = lazyScreen<ComponentProps<typeof BusinessFinancesScreenType>>(() => import("./workspaces/business/BusinessFinancesScreen"), "BusinessFinancesScreen");
const PortfolioScreen = lazyScreen<ComponentProps<typeof PortfolioScreenType>>(() => import("./workspaces/portfolio/PortfolioScreen"), "PortfolioScreen");
const DealsScreen = lazyScreen<ComponentProps<typeof DealsScreenType>>(() => import("./workspaces/deals/DealsScreen"), "DealsScreen");
const FindScreen = lazyScreen<ComponentProps<typeof FindScreenType>>(() => import("./workspaces/find/FindScreen"), "FindScreen");
const ProjectWorkspace = lazyScreen<ComponentProps<typeof ProjectWorkspaceType>>(() => import("./workspaces/project/ProjectWorkspace"), "ProjectWorkspace");
const SharedProjectScreen = lazyScreen<ComponentProps<typeof SharedProjectScreenType>>(() => import("./workspaces/shared/SharedProjectScreen"), "SharedProjectScreen");
const StandardsScreen = lazyScreen<ComponentProps<typeof StandardsScreenType>>(() => import("./workspaces/standards/StandardsScreen"), "StandardsScreen");
import { Building2, Compass, FileSpreadsheet, LineChart, Wallet } from "lucide-react";
import { PublicSite, navigateTo, publicPageFor } from "./public/PublicSite";
import {
  AccessWarningBanner,
  BillingScreen,
  LegalAcceptScreen,
  OnboardingScreen,
} from "./session/EarlyAccessScreens";

/**
 * Top-level navigation state. The everyday destinations — Projects, Deals,
 * Find and the Capital Plan — sit in the top bar (spec §87.17, §88.13); the rest
 * are reached from inside them or from the account menu.
 */
export type AppView = AppRoute;

/** Which screen an invitation or reset link asks for. */
function linkRoute(): { kind: "invite" | "reset" | "forgot" | "app"; token: string } {
  if (typeof window === "undefined") return { kind: "app", token: "" };
  const path = window.location.pathname.replace(/\/+$/, "");
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  if (path === "/invite") return { kind: "invite", token };
  if (path === "/reset") return { kind: "reset", token };
  if (path === "/forgot") return { kind: "forgot", token: "" };
  return { kind: "app", token: "" };
}

const currentPath = () => (typeof window === "undefined" ? "/" : window.location.pathname.replace(/\/+$/, "") || "/");

export default function App() {
  const [db, setDb] = useState<AppDatabase | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [nav, setNavState] = useState<AppView>(() => (currentPath() === "/billing" ? { view: "billing" } : parseAppRoute(window.location.hash)));
  const [navigationError, setNavigationError] = useState<string | null>(null);
  useNavigationScroll(appRouteHash(nav));
  const currentNav = useRef(nav);
  currentNav.current = nav;
  const navigationTicket = useRef(0);
  const historyIndex = useRef(navigationIndex98(window.history.state) ?? 0);
  const recoveringHistory = useRef(false);
  const beforeLeave = useRef<(() => Promise<void>) | null>(null);
  const registerBeforeLeave = useCallback((guard: (() => Promise<void>) | null) => { beforeLeave.current = guard; }, []);
  const setNav = useCallback((next: AppView) => {
    const departureScroll = window.scrollY;
    const ticket = ++navigationTicket.current;
    void (async () => {
    try { await beforeLeave.current?.(); } catch (error) { setNavigationError(userMessage(error, "Could not save your changes.")); return; }
    if (ticket !== navigationTicket.current) return;
    setNavigationError(null);
    saveNavigationScroll(departureScroll);
    const hash = appRouteHash(next);
    if (window.location.hash !== hash) {
      const previous = appRouteHash(currentNav.current);
      historyIndex.current += 1;
      window.history.pushState({nav98:historyIndex.current,return98:previous}, "", hash);
    }
    currentNav.current = next;
    setNavState(next);
    })();
  }, []);
  useEffect(() => {
    window.history.replaceState({...window.history.state,nav98:historyIndex.current}, "");
    let pendingTarget: string | null = null;
    const restore = () => {
      const target = parseAppRoute(window.location.hash);
      const targetIndex = navigationIndex98(window.history.state) ?? historyIndex.current + 1;
      const targetHash = appRouteHash(target);
      if (recoveringHistory.current && targetIndex === historyIndex.current) {
        recoveringHistory.current=false;
        window.dispatchEvent(new Event(HISTORY_VIEWS_RESTORED98));
        return;
      }
      if (targetHash === appRouteHash(currentNav.current) && targetIndex === historyIndex.current) return;
      const signature = `${targetIndex}:${targetHash}`;
      if (pendingTarget === signature) return;
      pendingTarget=signature;
      const ticket=++navigationTicket.current;
      void (async()=>{
        try { await beforeLeave.current?.(); }
        catch (error) {
          if(ticket!==navigationTicket.current)return;
          // Keep the active entry intact. Replacing the popped entry would erase
          // the very context Back was meant to restore, even after Keep editing.
          const delta=historyIndex.current-targetIndex;
          if(delta){recoveringHistory.current=true;window.history.go(delta);}
          else window.history.replaceState({...window.history.state,nav98:historyIndex.current},"",appRouteHash(currentNav.current));
          setNavigationError(userMessage(error,"Your unsaved entry is still open."));
          return;
        }
        if(ticket!==navigationTicket.current)return;
        historyIndex.current=targetIndex;
        window.history.replaceState({...window.history.state,nav98:targetIndex},"");
        currentNav.current=target;
        setNavigationError(null);
        setNavState(target);
        // Presentation state restores only AFTER the draft/pending-write guard.
        window.dispatchEvent(new Event(HISTORY_VIEWS_RESTORED98));
      })().finally(()=>{if(pendingTarget===signature)pendingTarget=null;});
    };
    window.addEventListener("popstate",restore);window.addEventListener("hashchange",restore);
    return()=>{window.removeEventListener("popstate",restore);window.removeEventListener("hashchange",restore);};
  }, []);
  // Phase 13A: the hosted deployment signs a tester in before the
  // application opens their workspace. The desktop has no session at all.
  const [hosted, setHosted] = useState<{
    checked: boolean;
    user: HostedAccount | null;
    isHosted: boolean;
    unreachable: boolean;
    /** The build the server is running; quoted in feedback and diagnostics. */
    version: string;
  }>({ checked: false, user: null, isHosted: false, unreachable: false, version: "" });
  const [route, setRoute] = useState(() => linkRoute());
  const [path, setPath] = useState(currentPath);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(false);
  /** Set when onboarding just finished, so the first project form opens by itself. */
  const [justOnboarded, setJustOnboarded] = useState(false);

  useEffect(() => {
    const onPop = () => {
      setPath(currentPath());
      setRoute(linkRoute());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchSession().then((session) => {
      if (cancelled) return;
      // The Design tab tells "my plan" from "somebody else's" by author.
      setCurrentAccountId(session?.user?.id ?? null);
      setHosted({
        checked: true,
        user: (session?.user as HostedAccount | null) ?? null,
        isHosted: session !== null,
        unreachable: session?.unreachable === true,
        version: session?.version ?? "",
      });
      // Telemetry and diagnostics exist ONLY in the hosted beta; the desktop
      // application records nothing about the person using it.
      enableTelemetry(session !== null && session.user !== null);
      if (session !== null) {
        installGlobalDiagnostics();
        void fetchPublicConfig().then((config) => {
          if (!cancelled) setSignupEnabled(config?.signupEnabled ?? false);
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = useCallback(async () => {
    await flushTelemetry();
    await logout();
    setDb(null);
    setNav({ view: "projects" });
    setHosted((previous) => ({ ...previous, checked: true, user: null, isHosted: true, unreachable: false }));
    navigateTo("/");
  }, []);

  /** Fold a fresh account state from the server into the signed-in user. */
  const applyState = useCallback((state: AccountState & { displayName?: string }) => {
    setHosted((previous) => (previous.user ? { ...previous, user: { ...previous.user, ...state } } : previous));
  }, []);

  // Every screen names itself in the browser tab (spec §87.31); the project
  // and deal screens refine it with their own name.
  useEffect(() => {
    const titles: Partial<Record<AppView["view"], string>> = {
      projects: "Projects",
      portfolio: "Capital Plan",
      business: "Business Finances",
      admin: "Admin",
      account: "Account",
      billing: "Billing",
      privacy: "What the app records",
      deals: "Deals",
      find: "Find",
    };
    // A terms or onboarding gate names its own tab; the view title waits until it is passed.
    if (hosted.user?.legal?.needsAcceptance || hosted.user?.onboardingComplete === false) return;
    const title = titles[nav.view];
    if (title) document.title = `${title} — Berrier Reno`;
    // Found in the §88B run: finishing onboarding left the tab reading
    // "Welcome", because the view itself never changed. Re-title when the
    // terms and onboarding gates are passed as well.
  }, [nav.view, hosted.user?.onboardingComplete, hosted.user?.legal?.needsAcceptance]);

  useEffect(() => {
    if (nav.view === "projects") track("area.viewed", undefined, "projects");
    if (nav.view === "admin") track("area.viewed", undefined, "admin");
    if (nav.view === "account") track("area.viewed", undefined, "account");
  }, [nav.view]);

  // In the hosted build a signed-in person opens the workspace only once they
  // have accepted the current terms, finished onboarding and have access —
  // the server enforces the last one regardless, this just avoids a
  // screenful of refusals.
  const user = hosted.user;
  const ready =
    !hosted.isHosted ||
    (user !== null &&
      user.legal?.needsAcceptance !== true &&
      user.onboardingComplete !== false &&
      user.access?.allowed !== false);

  useEffect(() => {
    if (!hosted.checked) return;
    if (hosted.isHosted && !ready) return;
    let cancelled = false;
    openAppDatabase()
      .then((opened) => {
        if (!cancelled) {
          setDb(opened);
          // dev preview debugging only — must be the LIVE instance
          // (StrictMode double-mount opens a second, discarded preview DB).
          if (opened.mode === "browser-preview") {
            (window as unknown as { __appDb?: unknown }).__appDb = opened;
          }
          // Development-only sample data for visual acceptance — the in-memory
          // preview, or a DISPOSABLE local server (scripts/dev-server-local.cmd).
          // import.meta.env.DEV is false in every production bundle.
          if (import.meta.env.DEV && opened.mode !== "tauri") {
            void import("./dev/seedDemo").then(({ seedDemo }) => {
              (window as unknown as { __seedDemo?: () => Promise<string> }).__seedDemo = () => seedDemo(opened);
            });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to open the Berrier Reno database:", err);
        if (!cancelled) {
          setInitError(
            "Berrier Reno could not open its database. " +
              "Close the application and try again. If the problem persists, " +
              "check the application data folder for a locked or damaged database file.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [hosted.checked, hosted.isHosted, ready]);

  const goToApp = () => {
    window.history.replaceState({}, "", "/");
    setRoute({ kind: "app", token: "" });
    setPath("/");
  };

  if (route.kind === "invite") return <AcceptInvitationScreen token={route.token} onActivated={goToApp} />;
  if (route.kind === "reset") return <ResetPasswordScreen token={route.token} onDone={goToApp} />;
  if (route.kind === "forgot") return <ForgotPasswordPanel onBack={goToApp} />;

  const publicPage = publicPageFor(path);

  /* ---------------- hosted, signed out: the public site ---------------- */
  if (hosted.checked && hosted.isHosted && !user) {
    if (publicPage && !hosted.unreachable) return <PublicSite page={publicPage} />;
    return (
      <SignInScreen
        serverUnreachable={hosted.unreachable}
        signupEnabled={signupEnabled}
        onForgotPassword={() => setRoute({ kind: "forgot", token: "" })}
        onSignedIn={(signedIn) => {
          enableTelemetry(true);
          setCurrentAccountId(signedIn.id ?? null);
          setHosted((previous) => ({ ...previous, checked: true, user: signedIn as HostedAccount, isHosted: true, unreachable: false }));
          if (publicPageFor(currentPath()) !== null || currentPath() === "/login") navigateTo("/");
        }}
      />
    );
  }

  /* ---------------- hosted, signed in: the Early Access gates ---------------- */
  if (hosted.checked && hosted.isHosted && user) {
    // The legal pages stay readable while signed in.
    if (publicPage === "terms" || publicPage === "privacy" || publicPage === "features" || publicPage === "pricing") {
      return <PublicSite page={publicPage} />;
    }
    if (user.legal?.needsAcceptance) return <LegalAcceptScreen onAccepted={applyState} />;
    if (user.onboardingComplete === false) {
      return (
        <OnboardingScreen
          user={user}
          onDone={(state) => {
            applyState(state);
            setJustOnboarded(true);
            track("onboarding_step_viewed", { source: "completed" }, "onboarding");
          }}
        />
      );
    }
    if (user.access && !user.access.allowed) {
      return (
        <BillingScreen
          state={user as AccountState}
          onState={applyState}
          onSignOut={() => void signOut()}
          onContinue={() => navigateTo("/")}
        />
      );
    }
  }

  if (initError) {
    return (
      <div className="app-shell">
        <header className="topbar">
          <span className="brand">Berrier Reno</span>
        </header>
        <main className="content">
          <div className="error-panel" role="alert">
            <h2>Startup problem</h2>
            <p>{initError}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!db) {
    return (
      <div className="app-shell">
        <header className="topbar">
          <span className="brand">Berrier Reno</span>
        </header>
        <main className="content">
          <p className="muted">Opening project database…</p>
        </main>
      </div>
    );
  }

  const accountState = user && user.access ? (user as AccountState) : null;

  return (
    <AppDbContext.Provider value={db}>
      <DraftNavigationProvider>
      <div className="app-shell">
        <header className="topbar">
          <button
            className="brand brand-button"
            onClick={() => {
              if (window.location.pathname !== "/") window.history.pushState({}, "", "/");
              setNav({ view: "projects" });
            }}
            title="Go to Projects"
          >
            <BrandMark size={26} />
            <span className="brand-name">Berrier Reno</span>
          </button>
          {(user?.profile?.companyName || db.mode !== "server") && (
            <span className="topbar-subtitle">
              {user?.profile?.companyName ? user.profile.companyName : "Berrier Holdings LLC"}
            </span>
          )}
          <nav className="primary-nav" aria-label="Main">
            {(
              [
                ["Projects", "projects", Building2],
                ["Deals", "deals", FileSpreadsheet],
                ["Find", "find", Compass],
                ["Capital Plan", "portfolio", LineChart],
                ["Business Finances", "business", Wallet],
              ] as const
            ).map(([label, view, Icon]) => {
              const active =
                nav.view === view ||
                (view === "projects" && (nav.view === "project" || nav.view === "shared"));
              return (
                <button
                  key={view}
                  className={`primary-nav-item ${active ? "primary-nav-active" : ""}`}
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    if (window.location.pathname !== "/") window.history.pushState({}, "", "/");
                    setNav({ view } as AppView);
                  }}
                >
                  <Icon size={16} aria-hidden="true" />
                  <span>{label}</span>
                </button>
              );
            })}
          </nav>
          <div className="mobile-workspace-switcher no-print">
            <label><span>Workspace</span><select aria-label="Workspace" value={nav.view === "project" || nav.view === "shared" ? "projects" : ["projects","deals","find","portfolio","business"].includes(nav.view) ? nav.view : ""} onChange={event=>setNav({view:event.target.value as "projects"|"deals"|"find"|"portfolio"|"business"})}>
              <option value="" disabled>Choose workspace</option><option value="projects">Projects</option><option value="deals">Deals</option><option value="find">Find</option><option value="portfolio">Capital Plan</option><option value="business">Business Finances</option>
            </select></label>
            {nav.view !== "shared" && <button className="btn btn-primary" onClick={()=>nav.view === "project" ? setNav({view:"project",projectId:nav.projectId,destination:"accounting",focusId:recordSpendingFocus()}) : setNav({view:"business",section:"expenses",focusId:"entry:spending"})}>Record spending</button>}
          </div>
          {db.mode === "browser-preview" && (
            <span className="preview-badge" title="Running outside the desktop shell; data is not saved.">
              Browser preview — data not saved
            </span>
          )}
          {db.mode === "server" && user && (
            <span className="topbar-end">
              <span className="beta-badge">Early Access</span>
              <AccountMenu
                name={user.displayName}
                email={user.email}
                isAdmin={user.role === "admin"}
                current={nav.view}
                onAccount={() => setNav({ view: "account" })}
                onBilling={accountState ? () => setNav({ view: "billing" }) : undefined}
                onAdmin={() => setNav({ view: "admin" })}
                onFeedback={() => setFeedbackOpen(true)}
                onPrivacy={() => setNav({ view: "privacy" })}
                onSignOut={() => void signOut()}
              />
            </span>
          )}
        </header>
        {accountState && <AccessWarningBanner state={accountState} />}
        <main className="content">
          {feedbackOpen && (
            <FeedbackDialog
              area={nav.view === "project" ? "project workspace" : nav.view}
              projectId={nav.view === "project" || nav.view === "shared" ? nav.projectId : null}
              onClose={() => setFeedbackOpen(false)}
            />
          )}
          {navigationError && <p role="alert">{navigationError} <button className="link-button" onClick={()=>setNavigationError(null)}>Dismiss</button></p>}
          <BetaErrorBoundary onReset={() => setNav({ view: "projects" })}>
          <Lazy>
          {nav.view === "admin" ? (
            <AdminScreen onClose={() => setNav({ view: "projects" })} />
          ) : nav.view === "billing" && accountState ? (
            <BillingScreen
              state={accountState}
              onState={applyState}
              embedded
              onContinue={() => {
                window.history.replaceState({}, "", "/");
                setNav({ view: "projects" });
              }}
            />
          ) : nav.view === "account" ? (
            <AccountScreen
              user={user ?? { email: "", displayName: "" }}
              version={hosted.version}
              onClose={() => setNav({ view: "projects" })}
              onStateChanged={applyState}
              onOpenBilling={() => setNav({ view: "billing" })}
            />
          ) : nav.view === "privacy" ? (
            <div>
              <PrivacyNotice />
              <button className="btn" onClick={() => setNav({ view: "projects" })}>
                Back to Berrier Reno
              </button>
            </div>
          ) : nav.view === "deals" ? (
            <DealsScreen
              key={nav.dealId ?? "list"}
              initialDealId={nav.dealId}
              onOpenProject={(projectId) => setNav({ view: "project", projectId })}
            />
          ) : nav.view === "standards" ? (
            <StandardsScreen onClose={() => setNav({ view: "projects" })} />
          ) : nav.view === "find" ? (
            <FindScreen onOpenDeal={(dealId) => setNav({ view: "deals", dealId })} />
          ) : nav.view === "business" ? (
            <BusinessFinancesScreen section={nav.section} focusId={nav.focusId} registerBeforeLeave={registerBeforeLeave} onNavigate={(section,focusId)=>setNav({view:"business",section,focusId})} onOpenCapitalPlan={()=>setNav({view:"portfolio"})} onOpenProject={(projectId,destination,focusId)=>setNav({view:"project",projectId,destination,focusId})}/>
          ) : nav.view === "portfolio" ? (
            <PortfolioScreen
              onOpenBusiness={(section, focusId) => setNav({ view: "business", section, focusId })}
              registerBeforeLeave={registerBeforeLeave}
              onClose={() => setNav({ view: "projects" })}
              onOpenProject={(projectId, destination, focusId) => setNav({ view: "project", projectId, destination, focusId })}
              onOpenDeals={() => setNav({ view: "deals" })}
            />
          ) : nav.view === "projects" || nav.view === "billing" ? (
            <ProjectsScreen
              registerBeforeLeave={registerBeforeLeave}
              onOpenProject={(projectId, destination, focusId) => setNav({ view: "project", projectId, destination, focusId })}
              onOpenShared={(projectId) => setNav({ view: "shared", projectId })}
              defaultStrategy={user?.profile?.primaryIntent === "rental" ? "rental" : "flip"}
              justOnboarded={justOnboarded}
              firstName={user?.displayName && user.displayName !== user.email ? user.displayName.split(/\s+/)[0] : undefined}
              onOpenDeals={() => setNav({ view: "deals" })}
              onOpenFind={() => setNav({ view: "find" })}
              onOpenStandards={() => setNav({ view: "standards" })}
            />
          ) : nav.view === "shared" ? (
            <SharedProjectScreen
              key={nav.projectId}
              initialDestination={nav.destination}
              registerBeforeLeave={registerBeforeLeave}
              onNavigate={(destination) => setNav({ view: "shared", projectId: nav.projectId, destination })}
              projectId={nav.projectId}
              onBack={() => setNav({ view: "projects" })}
            />
          ) : (
            <ProjectWorkspace
              key={nav.projectId}
              registerBeforeLeave={registerBeforeLeave}
              focusId={nav.focusId}
              onReturnToContext={()=>{const previous=safeReturn98(window.history.state?.return98);setNav(previous?parseAppRoute(previous):{view:"project",projectId:nav.projectId,destination:"overview"});}}
              initialDestination={nav.destination}
              onNavigate={(destination, focusId) => setNav({ view: "project", projectId: nav.projectId, destination, focusId })}
              projectId={nav.projectId}
              onBackToProjects={() => setNav({ view: "projects" })}
              onOpenCapitalPlan={() => setNav({ view: "portfolio" })}
              onOpenDeal={(dealId) => setNav({ view: "deals", dealId })}
            />
          )}
          </Lazy>
          </BetaErrorBoundary>
        </main>
        {db.mode === "server" && (
          <footer className="app-footer muted">
            <span>
              Early Access — features may change.
              {hosted.version && hosted.version !== "dev" && hosted.version !== "unknown" && (
                <>
                  {" "}
                  Version <code>{hosted.version}</code>
                </>
              )}
            </span>
            <span className="app-footer-links">
              <a href="/terms" target="_blank" rel="noreferrer">Terms</a>
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy</a>
              <button className="link-button" onClick={() => setNav({ view: "privacy" })}>
                What the app records
              </button>
            </span>
          </footer>
        )}
      </div>
      </DraftNavigationProvider>
    </AppDbContext.Provider>
  );
}
